import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  IconButton,
  Divider
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import theme from '../../../theme/theme.js';
import ShareIcon from '@mui/icons-material/Share';
import VideoCallIcon from '@mui/icons-material/VideoCall';
import DraftSaveButton from '../../../components/DraftSaveButton';
import { DRAFT_KEYS } from '../../../utils/draftUtils';

// Hooks
import { useConsentForm } from './hooks/useConsentForm';

// Components
import SignatureDialog from './components/SignatureDialog';

// Screens
import PatientDemographicsSection from './screens/PatientDemographicsSection';
import CarePlanSection from './screens/CarePlanSection';
import ConsentSignaturesSection from './screens/ConsentSignaturesSection';
import RepresentativeConsentSection from './screens/RepresentativeConsentSection';

const ConsentForAnaesthesia = () => {
  const { uhid_no } = useParams();
  const navigate = useNavigate();

  const {
    formData,
    setFormData,
    errors,
    loading,
    success,
    signatureDialogOpen,
    setSignatureDialogOpen,
    signaturePadRef,
    handleChange,
    handleNameChange,
    handleSignatureOpen,
    handleSignatureSave,
    handleSubmit,
    handleShare,
  } = useConsentForm(uhid_no, navigate);




  const handleBack = () => {
    navigate(`/mainPage/${uhid_no}`, {
      state: { from: 'PreOperativePhase', status: 'success', step: 1 },
    });
  };

  const handleHomeNavigation = () => {
    navigate('/home');
  };

  const goToVideoRecorder = () => {
    navigate(`/video?form=${encodeURIComponent("CONSENT FOR ANAESTHESIA")}&uhid_no=${encodeURIComponent(uhid_no)}`);
  };

  if (loading && !formData.patient_name) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: '#008080' }} />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <Container maxWidth="md" sx={{ marginTop: 4, marginBottom: 4 }}>
        <Paper elevation={3} sx={{ padding: 4, borderRadius: 3, border: '1px solid #e0e0e0' }}>
          {/* Header */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
            <Box>
              <Button
                onClick={handleBack}
                startIcon={<span>←</span>}
                sx={{ color: '#008080', fontWeight: 600, mb: 1, textTransform: 'none' }}
              >
                Back
              </Button>
              <Typography
                variant="h4"
                sx={{
                  color: '#008080',
                  fontWeight: 800,
                  textTransform: 'uppercase',
                  letterSpacing: 1
                }}
              >
                Consent for Anaesthesia
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <img src="/logo.png" alt="Logo" style={{ width: '32px', height: '32px' }} />
              <Typography variant="h6" sx={{ color: '#008080', fontWeight: 600 }}>OTMS</Typography>
            </Box>
          </Box>

          {/* Action Row */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, gap: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                variant="contained"
                onClick={handleHomeNavigation}
                sx={{ backgroundColor: '#008080', textTransform: 'none' }}
                >
                Home
                </Button>
                <Button
                variant="outlined"
                startIcon={<VideoCallIcon />}
                onClick={goToVideoRecorder}
                sx={{ color: '#008080', borderColor: '#008080', textTransform: 'none' }}
                >
                Add Iterative Video
                </Button>
            </Box>
            <IconButton color="primary" onClick={handleShare} sx={{ border: '1px solid #ccc' }}>
              <ShareIcon />
            </IconButton>
          </Box>

          <form onSubmit={handleSubmit}>
            <PatientDemographicsSection 
              formData={formData} 
              handleChange={handleChange} 
              handleNameChange={handleNameChange}
              errors={errors} 
            />

            <CarePlanSection 
              formData={formData} 
              handleChange={handleChange} 
              handleNameChange={handleNameChange}
              setFormData={setFormData}
              errors={errors}
            />

            <ConsentSignaturesSection
              formData={formData}
              handleSignatureOpen={handleSignatureOpen}
              handleNameChange={handleNameChange}
              handleChange={handleChange}
            />

            <RepresentativeConsentSection
              formData={formData}
              handleChange={handleChange}
              handleNameChange={handleNameChange}
              handleSignatureOpen={handleSignatureOpen}
              setFormData={setFormData}
            />

            {/* Submit / Draft Buttons */}
            <Box sx={{ mt: 6, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <DraftSaveButton
                formKey={DRAFT_KEYS.CONSENT_FOR_ANAESTHESIA}
                uhidNo={uhid_no}
                formData={formData}
                onDraftLoaded={(draftData) => setFormData(prev => ({ ...prev, ...draftData }))}
                variant="outlined"
                size="large"
                isUpdate={success}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{
                  backgroundColor: '#008080',
                  '&:hover': { backgroundColor: '#006666' },
                  px: 8,
                  py: 1.5,
                  borderRadius: 3,
                  fontSize: '1.1rem',
                  fontWeight: 700,
                  boxShadow: '0 4px 12px rgba(0, 128, 128, 0.3)',
                  textTransform: 'none'
                }}
              >
                {loading ? "Processing..." : (success ? "Update Consent Record" : "Submit Consent Record")}
              </Button>
            </Box>
          </form>

          {/* Dialogs */}
          <SignatureDialog
            open={signatureDialogOpen}
            onClose={() => setSignatureDialogOpen(false)}
            onSave={handleSignatureSave}
            signaturePadRef={signaturePadRef}
          />
        </Paper>
      </Container>
    </ThemeProvider>
  );
};

export default ConsentForAnaesthesia;
